import React, { useState, useEffect } from 'react';
import { Button } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { SEO } from '../SEO';
import seoConfig from '../SEO/seoConfig';
import { 
  faPlus, 
  faLightbulb,
  faFolder,
  faCalendar,
  faFileText,
  faArrowRight,
  faTimes,
  faRocket,
  faStar,
  faProjectDiagram,
  faEllipsisVertical,
  faTrash,
  faCopy,
  faDownload
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import Navigation from '../Home/Navigation';
import { useNotification } from '../../context/NotificationContext';
import { useAuth } from '../../context/Auth/AuthContext';
import { 
  fetchProjects,
  Project as ProjectType,
  deleteProject, 
  duplicateProject 
} from '../../services/projectService';
import { getApiUrl } from '../../config/api';
import styles from './PromptLandingPage.module.css';

interface Project {
  id: string;
  title: string;
  type: string;
  description?: string;
  createdAt: string;
  hasPrompt: boolean;
  metadata?: any;
}

const PromptLandingPage: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newProject, setNewProject] = useState({
    title: '',
    description: ''
  });
  const [isCreating, setIsCreating] = useState(false);
  
  // Search and filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'prompt'>('all');
  const [activeActionDropdown, setActiveActionDropdown] = useState<string | null>(null);
  
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const { token, user } = useAuth();

  useEffect(() => {
    // Only load projects if user is authenticated
    if (token && user) {
      loadProjects();
    } else {
      setLoading(false);
      showNotification({
        title: 'Authentication Required',
        message: 'Please log in to view your projects',
        type: 'error'
      });
    }
  }, [token, user]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      if (activeActionDropdown) {
        setActiveActionDropdown(null);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [activeActionDropdown]);

  const loadProjects = async () => {
    if (!token || !user) {
      console.warn('No authentication token or user found');
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      console.log('Loading all projects for user:', user.id);
      
      const response = await fetchProjects(1, 1000);

      if (response && 'projects' in response) {
        // Additional client-side filtering to ensure only user's projects are shown
        // (Backend should already handle this, but this is extra security)
        const userProjects = response.projects.filter((project: any) => {
          // If the project has a userId field, check it matches
          if (project.userId && project.userId !== user.id) {
            console.warn(`Project ${project.id} belongs to different user:`, project.userId, 'vs', user.id);
            return false;
          }
          return true;
        });

        console.log(`Loaded ${userProjects.length} projects for user ${user.id}`);
        setProjects(userProjects);
      } else {
        console.warn('No projects found in response');
        setProjects([]);
      }
    } catch (error) {
      console.error('Error loading projects:', error);
      showNotification({
        title: 'Error',
        message: 'Failed to load projects. Please try again.',
        type: 'error'
      });
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProject = async () => {
    if (!token || !user) {
      showNotification({
        title: 'Authentication Required',
        message: 'Please log in to create projects',
        type: 'error'
      });
      return;
    }

    if (!newProject.title.trim()) {
      showNotification({
        title: 'Error',
        message: 'Project title is required',
        type: 'error'
      });
      return;
    }

    setIsCreating(true);
    try {
      console.log('Creating project for user:', user.id);
      
      const response = await fetch(getApiUrl('/prompt-generator/create-project'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          title: newProject.title.trim(),
          description: newProject.description.trim() || undefined,
          userId: user.id // Explicitly include user ID
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success) {
        // showNotification({
        //   title: 'Success',
        //   message: 'Project created successfully!',
        //   type: 'success'
        // });
        
        // Close modal first
        setIsModalOpen(false);
        
        // Navigate directly to the prompt generator with project ID
        navigate(`/prompt-generator/${result.data.projectId}/prompt-generator`);
      } else {
        throw new Error(result.message || 'Failed to create project');
      }
    } catch (error) {
      console.error('Error creating project:', error);
      showNotification({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to create project',
        type: 'error'
      });
    } finally {
      setIsCreating(false);
    }
  };

  const handleProjectClick = (projectId: string) => {
    // Navigate to the project overview page
    navigate(`/prompt-generator/${projectId}`);
  };

  const openModal = () => {
    setIsModalOpen(true);
    setNewProject({ title: '', description: '' });
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setNewProject({ title: '', description: '' });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Filter and search projects
  const filteredProjects = projects.filter(project => {
    // Check if project has prompt in metadata or hasPrompt field
    const hasPrompt = project.hasPrompt || (project.metadata && project.metadata.hasPrompt) || false;
    
    // Apply type filter
    const typeMatch = filterType === 'all' || (filterType === 'prompt' && hasPrompt);
    
    // Apply search filter
    const searchMatch = searchTerm === '' || 
      project.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (project.description && project.description.toLowerCase().includes(searchTerm.toLowerCase()));
    
    return typeMatch && searchMatch;
  });

  // Toggle dropdown visibility
  const toggleActionDropdown = (projectId: string) => {
    setActiveActionDropdown(activeActionDropdown === projectId ? null : projectId);
  };

  // Handle project delete
  const handleDeleteProject = async (projectId: string) => {
    try {
      await deleteProject(projectId);
      setProjects(projects.filter(project => project.id !== projectId));
      setActiveActionDropdown(null);
      showNotification({
        title: 'Success',
        message: 'Project deleted successfully',
        type: 'success'
      });
    } catch (error) {
      console.error('Error deleting project:', error);
      showNotification({
        title: 'Error',
        message: 'Failed to delete project',
        type: 'error'
      });
    }
  };

  // Handle project duplicate
  const handleDuplicateProject = async (projectId: string) => {
    try {
      const duplicatedProject = await duplicateProject(projectId);
      setProjects(prev => [duplicatedProject, ...prev]);
      setActiveActionDropdown(null);
      showNotification({
        title: 'Success',
        message: 'Project duplicated successfully',
        type: 'success'
      });
    } catch (error) {
      console.error('Error duplicating project:', error);
      showNotification({
        title: 'Error',
        message: 'Failed to duplicate project',
        type: 'error'
      });
    }
  };

  return (
    <div className={styles.container}>
      <SEO
        title={seoConfig.promptGenerator.title}
        description={seoConfig.promptGenerator.description}
        keywords={seoConfig.promptGenerator.keywords}
        url="https://kingdomstudio.pro/prompt-generator"
      />
      <Navigation />
      
      <main className={styles.main}>
        {/* Header Section */}
        <div className={styles.header}>
          <h1 className={styles.title}>
            <FontAwesomeIcon icon={faLightbulb} className={styles.titleIcon} />
            AI Prompt Generator
          </h1>
          <p className={styles.subtitle}>
            Create powerful AI prompts for your content projects. Generate video scripts, image descriptions, music briefs, and complete creative packages.
          </p>
          
          {/* Action Buttons */}
          <div className={styles.headerActions}>
            <Button
              className={styles.createButton}
              onClick={openModal}
              leftSection={<FontAwesomeIcon icon={faPlus} />}
              size="lg"
            >
              Create Project
            </Button>
          </div>
        </div>

        {/* Projects Section */}
        <div className={styles.projectsSection}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Your Projects</h2>
            <div className={styles.searchAndFilter}>
              <div className={styles.searchBar}>
                <input
                  type="text"
                  placeholder="Search projects..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={styles.searchInput}
                />
              </div>
              
              <div className={styles.filterOptions}>
                <button 
                  className={`${styles.filterButton} ${filterType === 'all' ? styles.active : ''}`}
                  onClick={() => setFilterType('all')}
                >
                  All ({projects.length})
                </button>
                <button 
                  className={`${styles.filterButton} ${filterType === 'prompt' ? styles.active : ''}`}
                  onClick={() => setFilterType('prompt')}
                >
                  With Prompts ({projects.filter(p => p.hasPrompt || (p.metadata && p.metadata.hasPrompt)).length})
                </button>
              </div>
            </div>
          </div>

          {/* Content */}
          {loading ? (
            <div className={styles.loadingState}>
              <div className={styles.loadingSpinner}></div>
              <p>Loading your projects...</p>
            </div>
          ) : filteredProjects.length === 0 ? (
            <div className={styles.emptyState}>
              <FontAwesomeIcon icon={faLightbulb} className={styles.emptyIcon} />
              <h3>{projects.length === 0 ? 'No projects found' : 'No projects match your search'}</h3>
              <p>{projects.length === 0 ? 'Start a new prompt project to organize your AI-generated content ideas' : 'Try adjusting your search terms or filter settings'}</p>
              {projects.length === 0 && (
                <Button
                  className={styles.createButton}
                  onClick={openModal}
                  leftSection={<FontAwesomeIcon icon={faPlus} />}
                  size="lg"
                >
                  Create Project
                </Button>
              )}
            </div>
          ) : (
            <div className={styles.projectsGrid}>
              {filteredProjects.map((project) => (
                <div
                  key={project.id}
                  className={styles.projectCard}
                  onClick={() => handleProjectClick(project.id)}
                >
                  <div className={styles.projectHeader}>
                    <div className={styles.projectIconWrapper}>
                      <FontAwesomeIcon icon={faFileText} className={styles.projectIcon} />
                    </div>
                    <div className={styles.projectStatus}>
                      {project.hasPrompt && project.metadata && project.metadata.promptCount ? (
                        <span className={styles.promptCountLabel}>
                          <FontAwesomeIcon icon={faLightbulb} />
                          {project.metadata.promptCount} {project.metadata.promptCount === 1 ? 'prompt' : 'prompts'}
                        </span>
                      ) : project.hasPrompt ? (
                        <span className={styles.promptCountLabel}>
                          <FontAwesomeIcon icon={faLightbulb} />
                          1 prompt
                        </span>
                      ) : null}
                    </div>
                  </div>
                  
                  <div className={styles.projectContent}>
                    <h3 className={styles.projectTitle}>{project.title}</h3>
                    {project.description && (
                      <p className={styles.projectDescription}>{project.description}</p>
                    )}
                    
                    <div className={styles.projectMeta}>
                      <div className={styles.projectDate}>
                        <FontAwesomeIcon icon={faCalendar} />
                        <span>{formatDate(project.createdAt)}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className={styles.projectActions}>
                    <div className={styles.moreActions}>
                      <button 
                        className={styles.moreButton}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleActionDropdown(project.id);
                        }}
                      >
                        <FontAwesomeIcon icon={faEllipsisVertical} />
                      </button>
                      {activeActionDropdown === project.id && (
                        <div className={`${styles.actionDropdown} ${activeActionDropdown === project.id ? styles.active : ''}`}
                             onClick={(e) => e.stopPropagation()}
                        >
                          <button 
                            className={styles.actionButton}
                            onClick={() => handleDuplicateProject(project.id)}
                          >
                            <FontAwesomeIcon icon={faCopy} /> Duplicate
                          </button>
                          <button 
                            className={styles.actionButton}
                            onClick={() => handleDeleteProject(project.id)}
                          >
                            <FontAwesomeIcon icon={faTrash} /> Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className={styles.projectGlow}></div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Create Project Modal */}
      {isModalOpen && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            {/* Header */}
            <div className={styles.modalHeader}>
              <h2 className={styles.modalTitle}>
                <FontAwesomeIcon icon={faRocket} className={styles.modalTitleIcon} />
                Create New Prompt Project
              </h2>
              <button
                className={styles.modalCloseButton}
                onClick={closeModal}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            {/* Content */}
            <div className={styles.modalContent}>
              <div className={styles.inputGroup}>
                <label className={styles.inputLabel}>
                  Project Title *
                </label>
                <input
                  type="text"
                  placeholder="Enter your project title..."
                  value={newProject.title}
                  onChange={(e) => setNewProject({ ...newProject, title: e.target.value })}
                  className={styles.inputField}
                  autoFocus
                />
              </div>

              <div className={styles.inputGroup}>
                <label className={styles.inputLabel}>
                  Description (Optional)
                </label>
                <textarea
                  placeholder="Describe what this project is about..."
                  value={newProject.description}
                  onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
                  rows={3}
                  className={styles.textareaField}
                />
              </div>
            </div>

            {/* Actions */}
            <div className={styles.modalActions}>
              <button
                className={styles.modalCancelButton}
                onClick={closeModal}
              >
                Cancel
              </button>
              <button
                className={styles.modalCreateButton}
                onClick={handleCreateProject}
                disabled={isCreating || !newProject.title.trim()}
              >
                <FontAwesomeIcon icon={faPlus} />
                {isCreating ? 'Creating...' : 'Create Project'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PromptLandingPage;